import {
    Box,
    Step,
    StepContent,
    StepLabel,
    Stepper,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import LoadingButton from '@/components/Buttons/LoadingButton';
import useClientApprovalPageStore from '@/stores/useClientApprovalPageStore';
import { commonStyles, LawyerInfo, MobileLogo } from './ClientResponseContent';

interface Props {
    onAcceptClick(): void;
}

export default function ClientApprovalConfirmation({ onAcceptClick }: Props) {
    const theme = useTheme();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [searchParams] = useSearchParams();
    const logoFileId = searchParams.get('logoUrl');
    const lawFirmName = searchParams.get('lawfirmName');
    const intakeTemplateId = searchParams.get('intakeTemplateId');
    const lawFirmId = searchParams.get('lawFirmId');
    const lawyerPhone = searchParams.get('lawyerPhone');
    const email = searchParams.get('lawyerEmail');
    const lawyerName = searchParams.get('lawyerName');
    const prospectCreatedAs = searchParams.get('prospectCreatedAs');
    const quoteTemplateId = searchParams.get('quoteTemplateId');
    const templateType = searchParams.get('templateType');
    const { instanceId, prospectId } = useParams();
    const [approvalConfirmationStepper, acceptLoading] = useClientApprovalPageStore((state) => [
        state.approvalConfirmationStepper,
        state.acceptLoading,
    ]);

    const steps = [
        {
            label: 'Quote Acceptance',
            description: `By selecting "Accept" you retain us to assist you with your upcoming transaction.`,
            action: onAcceptClick,
            buttonLabel: 'Accept',
        },
        {
            label: 'Quote accepted',
            description: `You have successfully accepted the quote and ${
                lawFirmName || 'the law firm'
            } has been notified.
      <br />
      <br />
      Please proceed to the intake form which will capture the additional information we need to work on your file.`,
            action: () =>
                navigate(
                    `/intake/view/${intakeTemplateId}/${lawFirmId}?instanceId=${instanceId}&prospectId=${prospectId}&lawyerName=${lawyerName}&lawyerEmail=${email}&lawyerPhone=${lawyerPhone}&prospectCreatedAs=${prospectCreatedAs}&quoteTemplateId=${quoteTemplateId}&templateType=${templateType}`,
                ),
            buttonLabel: 'Go to intake form',
        },
    ];

    return (
        <Box sx={commonStyles.container}>
            <MobileLogo isMobile={isMobile} logoFileId={logoFileId} />
            <Stepper activeStep={approvalConfirmationStepper} orientation="vertical">
                {steps.map((s, index) => (
                    <Step key={s.label}>
                        <StepLabel>
                            <Typography variant={isMobile ? 'h3' : 'h1'}>{s.label}</Typography>
                        </StepLabel>
                        <StepContent>
                            <Typography
                                variant="body2"
                                style={{
                                    color: theme.palette.neutral.black,
                                    marginTop: '0px',
                                    fontSize: isMobile ? '12px' : '14px',
                                    lineHeight: isMobile ? '16px' : '20px',
                                }}
                                dangerouslySetInnerHTML={{
                                    __html: s.description,
                                }}
                            ></Typography>
                            {index !== 0 && <LawyerInfo />}
                            <LoadingButton
                                variant="contained"
                                loading={acceptLoading}
                                sx={{
                                    marginTop: '16px',
                                    padding: '8px 24px',
                                    width: isMobile ? '100%' : 'fit-content',
                                    textTransform: 'none',
                                }}
                                onClick={() => s.action()}
                            >
                                {s.buttonLabel}
                            </LoadingButton>
                        </StepContent>
                    </Step>
                ))}
            </Stepper>
        </Box>
    );
}
